import { useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate } from 'react-router-dom'
import { db, openOrderForTable, type Order, type DiningTable } from '../lib/db'
import { tableApi, type TableLayoutItem } from '../lib/api'
import { money } from '../lib/money'
import { isOwner, useApp } from '../lib/store'
import { useNow } from '../lib/hooks'
import { FloorPlan, GRID_COLS, type Placement } from '../components/FloorPlan'
import { TableGrid } from '../components/TableGrid'
import { TableEditor } from '../components/TableEditor'
import { ReserveSheet } from '../components/ReserveSheet'
import { AppBar } from '../components/AppBar'

export function TablesScreen() {
  const nav = useNavigate()
  const say = useApp((s) => s.say)
  const shop = useApp((s) => s.shop)
  const owner = isOwner(useApp((s) => s.session?.role))
  const refreshCatalog = useApp((s) => s.refreshCatalog)
  const now = useNow(30_000)

  const [zoneId, setZoneId] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [editorFor, setEditorFor] = useState<DiningTable | null>(null)
  const [reserveFor, setReserveFor] = useState<DiningTable | null>(null)
  const [dirty, setDirty] = useState<Map<string, Placement>>(new Map())

  const zones = useLiveQuery(() => db.zones.orderBy('sortOrder').toArray(), [], [])
  const allTables = useLiveQuery(() => db.diningTables.orderBy('sortOrder').toArray(), [], [])
  const openOrders = useLiveQuery(
    () => db.orders.where('status').equals('Open').toArray(),
    [],
    [] as Order[],
  )

  const activeZone = zoneId ?? zones[0]?.id ?? null

  // Chế độ lưới là mặc định. Quán nào chưa đụng tới cài đặt thì rơi vào đây.
  const isFloor = shop?.tableLayoutMode === 'Floor'
  const columns: 2 | 3 = shop?.gridColumns === 3 ? 3 : 2

  // Vị trí đang kéo dở đè lên vị trí đã lưu, để ô bàn không nhảy về chỗ cũ
  // trong lúc chờ máy chủ trả lời.
  const tables = useMemo(() => {
    return allTables
      .filter((t) => t.zoneId === activeZone)
      .map((t) => {
        const patch = dirty.get(t.id)
        return patch ? { ...t, ...patch } : t
      })
  }, [allTables, activeZone, dirty])

  const ordersByTable = useMemo(() => {
    const map = new Map<string, Order>()
    for (const o of openOrders) if (o.tableId) map.set(o.tableId, o)
    return map
  }, [openOrders])

  const busyCount = tables.filter((t) => ordersByTable.has(t.id)).length
  const holding = tables.reduce((sum, t) => sum + (ordersByTable.get(t.id)?.total ?? 0), 0)

  async function pickTable(t: DiningTable) {
    if (editing) {
      setEditorFor(t)
      return
    }
    const order = await openOrderForTable(t)
    nav(`/order/${order.id}`)
  }

  function moveTable(id: string, next: Placement) {
    setDirty((prev) => new Map(prev).set(id, next))
    void db.diningTables.update(id, next)
  }

  async function switchMode(mode: 'Grid' | 'Floor') {
    try {
      await tableApi.setMode(mode, columns)
      await refreshCatalog()
      say(mode === 'Floor' ? 'Giờ kéo thả được vị trí bàn' : 'Đã về sơ đồ lưới')
    } catch {
      say('Cần có mạng để đổi kiểu sơ đồ')
    }
  }

  async function setColumns(cols: 2 | 3) {
    try {
      await tableApi.setMode('Grid', cols)
      await refreshCatalog()
    } catch {
      say('Cần có mạng để đổi số cột')
    }
  }

  async function saveLayout() {
    const items: TableLayoutItem[] = [...dirty.entries()].map(([id, p]) => ({ id, ...p }))
    setEditing(false)

    if (items.length === 0) return

    try {
      await tableApi.saveLayout(items)
      setDirty(new Map())
      say('Đã lưu sơ đồ bàn')
    } catch {
      // Sơ đồ đã ghi vào máy rồi, mất mạng chỉ là chưa đẩy lên được.
      say('Chưa có mạng — sơ đồ đã lưu trên máy này')
    }
  }

  return (
    <div className="screen">
      <AppBar
        title="Sơ đồ bàn"
        sub={
          busyCount > 0
            ? `${busyCount} bàn có khách · đang treo ${money(holding)}`
            : 'Tất cả bàn đang trống'
        }
        onBack={() => nav('/')}
        right={
          editing ? (
            <button type="button" className="btn btn-brand btn-sm" onClick={saveLayout}>
              Xong
            </button>
          ) : owner ? (
            <button
              type="button"
              className="icon-btn"
              onClick={() => setEditing(true)}
              aria-label="Sắp xếp bàn"
              title="Sắp xếp bàn"
            >
              ✥
            </button>
          ) : undefined
        }
      />

      <div className="body">
        <div className="tabs tabs-inline" role="tablist" aria-label="Khu vực">
          {zones.map((z) => (
            <button
              key={z.id}
              type="button"
              role="tab"
              aria-selected={z.id === activeZone}
              onClick={() => setZoneId(z.id)}
            >
              {z.name}
            </button>
          ))}
        </div>

        {editing && (
          <div className="mode-box">
            <div className="field">
              <span>Kiểu sơ đồ</span>
              <div className="seg">
                <button type="button" aria-pressed={!isFloor} onClick={() => void switchMode('Grid')}>
                  ▦ Lưới đều
                </button>
                <button type="button" aria-pressed={isFloor} onClick={() => void switchMode('Floor')}>
                  ✥ Tự xếp
                </button>
              </div>
            </div>

            {isFloor ? (
              <p className="note">
                Kéo ô bàn tới đúng chỗ trên mặt bằng thật. Dùng phím mũi tên cũng dời được. Chạm
                vào bàn để đổi tên, biểu tượng, màu hoặc đặt bàn trước.
              </p>
            ) : (
              <>
                <div className="field">
                  <span>Số cột</span>
                  <div className="seg">
                    {([2, 3] as const).map((c) => (
                      <button
                        key={c}
                        type="button"
                        aria-pressed={columns === c}
                        onClick={() => void setColumns(c)}
                      >
                        {c} cột
                      </button>
                    ))}
                  </div>
                </div>
                <p className="note">
                  Bàn xếp đều theo thứ tự, không cần vẽ vị trí. Chạm vào bàn để đổi tên, biểu
                  tượng, màu hoặc đặt bàn trước.
                </p>
              </>
            )}
          </div>
        )}

        {tables.length === 0 ? (
          <div className="empty">
            <div className="empty-icon" aria-hidden="true">
              🪑
            </div>
            <p>Khu vực này chưa có bàn nào.</p>
            {owner && (
              <button type="button" className="btn btn-brand" onClick={() => setEditing(true)}>
                Thêm bàn
              </button>
            )}
          </div>
        ) : isFloor ? (
          <FloorPlan
            tables={tables}
            openOrders={ordersByTable}
            now={now}
            editing={editing}
            selectedId={editorFor?.id}
            onPick={pickTable}
            onMove={moveTable}
          />
        ) : (
          <TableGrid
            tables={tables}
            openOrders={ordersByTable}
            now={now}
            columns={columns}
            editing={editing}
            selectedId={editorFor?.id}
            onPick={pickTable}
          />
        )}
      </div>

      {editorFor && (
        <TableEditor
          table={editorFor}
          zones={zones}
          onReserve={() => {
            setReserveFor(editorFor)
            setEditorFor(null)
          }}
          onClose={() => setEditorFor(null)}
        />
      )}

      {reserveFor && <ReserveSheet table={reserveFor} onClose={() => setReserveFor(null)} />}

      {editing && (
        <div className="dock">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              const zone = activeZone
              if (!zone) return
              const used = tables.length
              void db.diningTables.add({
                id: crypto.randomUUID(),
                zoneId: zone,
                name: `Bàn ${used + 1}`,
                sortOrder: used,
                isTakeaway: false,
                icon: '🪑',
                color: null,
                gridX: used % GRID_COLS,
                gridY: Math.floor(used / GRID_COLS),
                gridW: 1,
                gridH: 1,
                seats: 4,
                reservedAt: null,
                reservedName: null,
                reservedPartySize: 0,
                reservedNote: null,
              })
            }}
          >
            + Thêm bàn
          </button>
          <button type="button" className="btn btn-brand grow" onClick={saveLayout}>
            Xong
          </button>
        </div>
      )}
    </div>
  )
}
